import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  OpportunityStatus,
  Permission,
  QuoteResponse,
  MAX_OPPORTUNITY_PROBABILITY,
  MIN_OPPORTUNITY_PROBABILITY,
} from "@forgepoint/shared";
import { useHasAnyPermission } from "@/store/authStore";
import { useOpportunityStagesStore } from "./opportunityStagesStore";
import type { CustomFieldsSchema, CustomFieldValues } from "@forgepoint/shared";
import { CustomFieldsSection } from "@/components/customFields/CustomFieldsSection";
import { CompanySelector } from "@/components/CompanySelector";
import { QuoteSelector } from "@/components/QuoteSelector";
import { CurrencyInput } from "@/components/CurrencyInputDecimal";
import { UserSelector } from "@/components/UserSelector";
import { PeopleSelector } from "@/components/PeopleSelector";
import { LocationSelector } from "@/components/LocationSelector";
import { Loader, Archive, Plus, X, ExternalLink } from "lucide-react";
import { useRecordQuotesStore } from "@/modules/quotes/quotesStore";
import { formatConvertedAmount } from "@/lib/quoteCurrency";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { useStageRequirements } from "./useStageRequirements";
import { focusStageField, getFieldPlaceholder } from "./stageRequirements";
import { StageReadiness } from "./components/StageReadiness";
import { RequiredMark } from "./components/RequiredMark";

export interface OpportunityFormData {
  name: string;
  companyId: string | null;
  salesRepUserId: string | null;
  estimatorUserIds: string[];
  personIds: string[];
  locationId: string | null;
  amount: string;
  status: OpportunityStatus;
  stageId: string | null;
  probability: number;
  customFields: CustomFieldValues;
}

interface OpportunityEditorSheetProps {
  formData: OpportunityFormData;
  onFormDataChange: (formData: OpportunityFormData) => void;
  mode: "create" | "edit";
  opportunityId?: string;
  onSave: () => Promise<true | string>;
  onCancel: () => void;
  onArchive?: () => void;
  isSaving: boolean;
  isArchiving: boolean;
  showLocationField?: boolean;
  customFieldsSchema?: CustomFieldsSchema | null;
}

/**
 * Dropdown selector for opportunity stages, auto-setting category and probability.
 * @param formData - Current form data
 * @param onFormDataChange - Callback to update form data
 */
function StageSelector({
  formData,
  onFormDataChange,
}: {
  formData: OpportunityFormData;
  onFormDataChange: (formData: OpportunityFormData) => void;
}) {
  const { getActiveStages, getStage } = useOpportunityStagesStore();

  return (
    <div className="grid gap-2">
      <Label htmlFor="stage">Stage *</Label>
      <Select
        value={formData.stageId ?? ""}
        onValueChange={(stageId: string) => {
          const stage = getStage(stageId);
          if (!stage) {
            return;
          }
          const probabilityByCategory: Partial<
            Record<OpportunityStatus, number>
          > = {
            ACCEPTED: MAX_OPPORTUNITY_PROBABILITY,
            LOST: MIN_OPPORTUNITY_PROBABILITY,
          };
          onFormDataChange({
            ...formData,
            stageId,
            ...(stage.category ? { status: stage.category } : {}),
            probability:
              (stage.category
                ? probabilityByCategory[stage.category]
                : undefined) ??
              stage.probability ??
              formData.probability,
          });
        }}
      >
        <SelectTrigger id="stage">
          <SelectValue placeholder="Select a stage" />
        </SelectTrigger>
        <SelectContent>
          {getActiveStages().map((stage) => (
            <SelectItem key={stage.id} value={stage.id}>
              {stage.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

/**
 * Form component for creating or editing opportunity details.
 * Displays form fields for opportunity name, company, amount, stage, and linked quotes.
 * Provides action buttons for save, cancel, and archive operations.
 * Linked quotes are clickable and navigate to the quotes page with the selected quote opened.
 */
export const OpportunityEditorSheet: React.FC<OpportunityEditorSheetProps> = ({
  formData,
  onFormDataChange,
  mode,
  opportunityId,
  onSave,
  onCancel,
  onArchive,
  isSaving,
  isArchiving,
  showLocationField = false,
  customFieldsSchema,
}) => {
  const navigate = useNavigate();
  const stageRequirements = useStageRequirements(
    formData.stageId,
    formData,
    customFieldsSchema,
  );
  const canViewPeople = useHasAnyPermission(
    Permission.PEOPLE_VIEW_ALL,
    Permission.PEOPLE_VIEW_OWN,
  );
  const { quotes, fetchQuotes, updateQuote, listLoading } =
    useRecordQuotesStore();
  const [linkedQuoteIds, setLinkedQuoteIds] = useState<string[]>([]);
  const [showQuoteSelector, setShowQuoteSelector] = useState(false);
  const [selectedQuoteIdToLink, setSelectedQuoteIdToLink] = useState<
    string | null
  >(null);
  const [isLinking, setIsLinking] = useState(false);
  const [unlinkingQuoteIds, setUnlinkingQuoteIds] = useState<Set<string>>(
    new Set(),
  );

  useEffect(() => {
    if (mode === "edit" && opportunityId) {
      fetchQuotes({ opportunityId });
    }
  }, [opportunityId, mode, fetchQuotes]);

  useEffect(() => {
    if (mode === "edit" && opportunityId) {
      const linkedQuotes = Array.from(quotes.values()).filter(
        (quote) => quote.opportunityId === opportunityId,
      );
      setLinkedQuoteIds(linkedQuotes.map((q) => q.id));
    }
  }, [quotes, opportunityId, mode]);

  const linkedQuotes = linkedQuoteIds
    .map((id) => quotes.get(id))
    .filter((q): q is QuoteResponse => q !== undefined);

  const handleLinkQuote = async () => {
    if (!selectedQuoteIdToLink || !opportunityId) {
      return;
    }

    setIsLinking(true);
    try {
      const result = await updateQuote(selectedQuoteIdToLink, {
        opportunityId,
      });

      if (result.error) {
        toast.error(result.error);
      } else {
        toast.success("Quote linked to opportunity");
        setShowQuoteSelector(false);
        setSelectedQuoteIdToLink(null);
        await fetchQuotes({ opportunityId });
      }
    } catch {
      toast.error("Failed to link quote");
    } finally {
      setIsLinking(false);
    }
  };

  const handleUnlinkQuote = async (quoteId: string) => {
    if (!opportunityId) {
      return;
    }

    setUnlinkingQuoteIds((prev) => new Set(prev).add(quoteId));
    try {
      const result = await updateQuote(quoteId, {
        opportunityId: null,
      });

      if (result.error) {
        toast.error(result.error);
      } else {
        toast.success("Quote removed from opportunity");
        await fetchQuotes({ opportunityId });
      }
    } catch {
      toast.error("Failed to unlink quote");
    } finally {
      setUnlinkingQuoteIds((prev) => {
        const next = new Set(prev);
        next.delete(quoteId);
        return next;
      });
    }
  };

  const getQuoteDisplayName = (quote: QuoteResponse) => {
    let display = quote.quoteDisplayNumber;
    if (quote.revision > 0) {
      display += ` Rev ${quote.revision}`;
    }
    if (quote.name) {
      display += ` - ${quote.name}`;
    }
    return display;
  };

  const getQuoteSecondaryInfo = (quote: QuoteResponse) => {
    const parts = [];
    if (quote.status) {
      parts.push(quote.status.toLowerCase().replace("_", " "));
    }
    if (quote.total) {
      parts.push(formatConvertedAmount(quote.total, quote));
    }
    return parts.join(" • ");
  };

  const handleQuoteClick = (quoteId: string) => {
    navigate("/quotes", { state: { openQuoteId: quoteId } });
  };

  /**
   * Calls onSave and displays a success or error toast based on the result.
   */
  const handleSave = async () => {
    const result = await onSave();
    if (result === true) {
      toast.success(
        mode === "create"
          ? "Opportunity created successfully"
          : "Opportunity updated successfully",
      );
    } else {
      toast.error(result);
    }
  };

  return (
    <>
      <div className="grid gap-4">
        <div className="grid gap-2">
          <Label htmlFor="name">Name *</Label>
          <Input
            id="name"
            value={formData.name}
            onChange={(e) =>
              onFormDataChange({ ...formData, name: e.target.value })
            }
            placeholder="Enter opportunity name"
          />
        </div>

        <div
          className={cn(
            "grid gap-2",
            stageRequirements.getStandardFieldClassName("companyId"),
          )}
          data-stage-field="companyId"
        >
          <Label htmlFor="company">
            Company
            {stageRequirements.isStandardRequired("companyId") && (
              <RequiredMark />
            )}
          </Label>
          <CompanySelector
            currentlySelectedId={formData.companyId}
            onSelectId={(companyId: string | null) =>
              onFormDataChange({
                ...formData,
                companyId: companyId,
              })
            }
            placeholder={getFieldPlaceholder(
              "Select a company",
              stageRequirements.isStandardRequired("companyId"),
            )}
          />
        </div>

        <div
          className={cn(
            "grid gap-2",
            stageRequirements.getStandardFieldClassName("salesRepUserId"),
          )}
          data-stage-field="salesRepUserId"
        >
          <Label htmlFor="salesRep">
            Sales Rep
            {stageRequirements.isStandardRequired("salesRepUserId") && (
              <RequiredMark />
            )}
          </Label>
          <UserSelector
            currentlySelectedId={formData.salesRepUserId}
            onSelectId={(salesRepUserId: string | null) =>
              onFormDataChange({ ...formData, salesRepUserId })
            }
            placeholder={getFieldPlaceholder(
              "Select a sales rep",
              stageRequirements.isStandardRequired("salesRepUserId"),
            )}
          />
        </div>

        <div
          className={cn(
            "grid gap-2",
            stageRequirements.getStandardFieldClassName("estimatorUserIds"),
          )}
          data-stage-field="estimatorUserIds"
        >
          <Label htmlFor="estimators">
            Estimators
            {stageRequirements.isStandardRequired("estimatorUserIds") && (
              <RequiredMark />
            )}
          </Label>
          <UserSelector
            mode="multi"
            selectedUserIds={formData.estimatorUserIds}
            onUserIdsChange={(estimatorUserIds: string[]) =>
              onFormDataChange({ ...formData, estimatorUserIds })
            }
            placeholder={getFieldPlaceholder(
              "Select estimators",
              stageRequirements.isStandardRequired("estimatorUserIds"),
            )}
          />
        </div>

        {canViewPeople && (
          <div className="grid gap-2">
            <Label htmlFor="people">People</Label>
            <PeopleSelector
              mode="multi"
              selectedPersonIds={formData.personIds}
              onPersonIdsChange={(personIds) =>
                onFormDataChange({ ...formData, personIds })
              }
              placeholder="Select people (optional)"
            />
          </div>
        )}

        {(showLocationField ||
          stageRequirements.isStandardRequired("locationId")) && (
          <div
            className={cn(
              "grid gap-2",
              stageRequirements.getStandardFieldClassName("locationId"),
            )}
            data-stage-field="locationId"
          >
            <Label htmlFor="location">
              Location
              {stageRequirements.isStandardRequired("locationId") && (
                <RequiredMark />
              )}
            </Label>
            <LocationSelector
              currentlySelectedId={formData.locationId}
              onSelectId={(locationId: string | null) =>
                onFormDataChange({ ...formData, locationId })
              }
              placeholder={getFieldPlaceholder(
                "Select a location",
                stageRequirements.isStandardRequired("locationId"),
              )}
            />
          </div>
        )}

        <div
          className={cn(
            "grid gap-2",
            stageRequirements.getStandardFieldClassName("amount"),
          )}
          data-stage-field="amount"
        >
          <Label htmlFor="amount">
            Amount
            {stageRequirements.isStandardRequired("amount") ? (
              <RequiredMark />
            ) : (
              " *"
            )}
          </Label>
          <CurrencyInput
            id="amount"
            value={formData.amount}
            onChange={(value) =>
              onFormDataChange({ ...formData, amount: value || "0.00" })
            }
            placeholder="0.00"
            prefix="$"
          />
        </div>

        <div className="grid gap-2">
          <Label htmlFor="probability">Probability</Label>
          <div className="flex items-center gap-2">
            <Input
              id="probability"
              type="number"
              min={0}
              max={100}
              value={formData.probability}
              onChange={(e) =>
                onFormDataChange({
                  ...formData,
                  probability: Math.min(
                    100,
                    Math.max(0, parseInt(e.target.value, 10) || 0),
                  ),
                })
              }
              className="w-24"
            />
            <span className="text-sm text-muted-foreground">%</span>
          </div>
        </div>

        <div>
          <StageSelector
            formData={formData}
            onFormDataChange={onFormDataChange}
          />
          {stageRequirements.stage && (
            <StageReadiness
              stageName={stageRequirements.stage.name}
              fields={stageRequirements.fields}
              onSelectField={focusStageField}
            />
          )}
        </div>

        {customFieldsSchema &&
          Object.keys(customFieldsSchema.properties).length > 0 && (
            <CustomFieldsSection
              schema={customFieldsSchema}
              values={formData.customFields}
              readOnly={false}
              additionalRequiredNames={stageRequirements.customRequiredNames}
              getFieldClassName={stageRequirements.getCustomFieldClassName}
              onChange={(fieldName, value) =>
                onFormDataChange({
                  ...formData,
                  customFields: {
                    ...formData.customFields,
                    [fieldName]: value,
                  },
                })
              }
            />
          )}

        {mode === "edit" && opportunityId && (
          <div className="grid gap-2 pt-2">
            <div className="flex items-center justify-between">
              <Label>Linked Quotes</Label>
              {!showQuoteSelector && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowQuoteSelector(true)}
                  type="button"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Link Quote
                </Button>
              )}
            </div>

            {showQuoteSelector && (
              <div className="border rounded-lg p-4 space-y-3 bg-muted/50">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium">Select a quote to link:</p>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setShowQuoteSelector(false);
                      setSelectedQuoteIdToLink(null);
                    }}
                    disabled={isLinking}
                    type="button"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                <QuoteSelector
                  currentlySelectedId={selectedQuoteIdToLink}
                  onSelectId={setSelectedQuoteIdToLink}
                  placeholder="Select a quote..."
                  disabled={isLinking}
                  unlinkedOnly
                />
                <Button
                  onClick={handleLinkQuote}
                  disabled={!selectedQuoteIdToLink || isLinking}
                  className="w-full"
                  type="button"
                >
                  {isLinking && (
                    <Loader className="h-4 w-4 mr-2 animate-spin" />
                  )}
                  Link Quote
                </Button>
              </div>
            )}

            {listLoading && linkedQuotes.length === 0 ? (
              <div className="flex items-center justify-center p-4 border rounded-lg">
                <Loader className="h-4 w-4 animate-spin text-muted-foreground" />
              </div>
            ) : linkedQuotes.length === 0 ? (
              <div className="text-center p-4 border rounded-lg text-muted-foreground text-sm">
                No quotes linked yet
              </div>
            ) : (
              <div className="space-y-2">
                {linkedQuotes.map((quote) => (
                  <div
                    key={quote.id}
                    className="flex items-center justify-between p-3 border rounded-lg"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate text-sm">
                        {getQuoteDisplayName(quote)}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {getQuoteSecondaryInfo(quote)}
                      </p>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleQuoteClick(quote.id)}
                        className="ml-2"
                        type="button"
                      >
                        <ExternalLink className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleUnlinkQuote(quote.id)}
                        disabled={unlinkingQuoteIds.has(quote.id)}
                        type="button"
                      >
                        {unlinkingQuoteIds.has(quote.id) ? (
                          <Loader className="h-4 w-4 animate-spin" />
                        ) : (
                          <X className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="mt-4 flex w-full justify-between">
        <div>
          {mode === "edit" && onArchive && (
            <Button
              variant="outline"
              onClick={onArchive}
              disabled={isSaving || isArchiving}
            >
              <Archive className="h-4 w-4 mr-2" />
              Archive
            </Button>
          )}
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={onCancel}
            disabled={isSaving || isArchiving}
          >
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isSaving || isArchiving}>
            {isSaving && <Loader className="h-4 w-4 mr-2 animate-spin" />}
            {mode === "create" ? "Create Opportunity" : "Save Changes"}
          </Button>
        </div>
      </div>
    </>
  );
};
